/**
 * cashBook — the Payments CASH-BOOK ledger (owner redesign, 2026-07-03).
 *
 * Owner: "Payments needs a cash-book style list — compact ledger rows — with
 * the option to open and edit any entry." The `payments/` collection is the
 * ledger (every receipt: CF gate-clearing docs + client-SDK partials);
 * firestore.rules already grants the team full read/write on it, so listing
 * AND editing need NO rules change.
 *
 * PURE core (row mapping, filtering, the edit body) + a thin Firestore seam
 * (listen + update). EDIT SCOPE (deliberate): amount, method, reference and
 * the USD-settlement INR figure — the bookkeeping facts. Editing an amount
 * does NOT re-run the money gate (gate state was decided when the payment was
 * recorded); the editor says so in plain language — corrections to WHAT was
 * received are bookkeeping, a NEW receipt goes through Record payment.
 */
import {
  collection,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import type { Currency } from './firms';
import type { UiMethod } from './recordPayment';

/** One ledger row — a `payments/{id}` doc, defensively typed. */
export interface CashBookEntry {
  id: string;
  projectId: string;
  gateId: string;
  /** The GATE-currency amount (field name is legacy — `currency` says what unit). */
  amountINR: number;
  currency: Currency;
  uiMethod: string;
  method: string;
  reference: string;
  /** partial = ledger-only (gate stayed open); else it cleared its gate. */
  partial: boolean;
  /** USD receipts — the INR the bank actually credited (0 = not recorded). */
  amountSettledINR: number;
  recordedByName: string;
  /** epoch ms of the receipt (at | createdAt | paidAt, first readable), 0 unknown. */
  atMs: number;
}

/** PURE — a Firestore timestamp-ish value → epoch ms (0 when unreadable). */
function toMs(v: unknown): number {
  if (v == null) return 0;
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const t = Date.parse(v);
    return Number.isFinite(t) ? t : 0;
  }
  const o = v as { toMillis?: () => number; seconds?: number };
  if (typeof o.toMillis === 'function') {
    try {
      return o.toMillis();
    } catch {
      return 0;
    }
  }
  if (typeof o.seconds === 'number') return o.seconds * 1000;
  return 0;
}

/** PURE — map a raw payments doc to a ledger entry (never throws). */
export function cashBookEntryFrom(
  id: string,
  d: Record<string, unknown>,
): CashBookEntry {
  return {
    id,
    projectId: typeof d.projectId === 'string' ? d.projectId : '',
    gateId: typeof d.gateId === 'string' ? d.gateId : '',
    amountINR: Number(d.amountINR) || 0,
    currency: d.currency === 'USD' ? 'USD' : 'INR',
    uiMethod: typeof d.uiMethod === 'string' ? d.uiMethod : '',
    method: typeof d.method === 'string' ? d.method : '',
    reference: typeof d.reference === 'string' ? d.reference : '',
    partial: d.partial === true,
    amountSettledINR: Number(d.amountSettledINR) || 0,
    recordedByName:
      typeof d.recordedByName === 'string' && d.recordedByName
        ? d.recordedByName
        : typeof d.recordedBy === 'string'
          ? (d.recordedBy as string)
          : '',
    atMs: toMs(d.at ?? d.createdAt ?? d.paidAt),
  };
}

/** PURE — newest first; unknown dates sink to the bottom (never fake recency). */
export function sortCashBook(rows: CashBookEntry[]): CashBookEntry[] {
  return [...rows].sort((a, b) => b.atMs - a.atMs);
}

/** PURE — free-text filter over VP / gate / reference / method / recorder. */
export function searchCashBook(
  rows: CashBookEntry[],
  q: string,
  labelOf: (projectId: string) => string,
): CashBookEntry[] {
  const s = q.trim().toLowerCase();
  if (!s) return rows;
  return rows.filter((r) =>
    `${r.projectId} ${labelOf(r.projectId)} ${r.gateId} ${r.reference} ${r.uiMethod} ${r.method} ${r.recordedByName}`
      .toLowerCase()
      .includes(s),
  );
}

/** The bookkeeping facts an admin may correct on a ledger entry. */
export interface CashBookEdit {
  amount: number;
  uiMethod: UiMethod | string;
  reference: string;
  /** USD receipts only — the INR the bank credited. Ignored for INR rows. */
  settledINR?: number | null;
}

/** PURE — validate + normalise the edit into the exact update body. Throws on
 *  invalid input so a bad write never reaches Firestore. */
export function cashBookEditBody(
  entry: CashBookEntry,
  edit: CashBookEdit,
  actorName: string,
): Record<string, unknown> {
  const amount = Number(edit.amount);
  if (!Number.isFinite(amount) || amount <= 0) {
    throw new Error('Amount must be a positive number.');
  }
  const body: Record<string, unknown> = {
    amountINR: Math.round(amount * 100) / 100,
    uiMethod: String(edit.uiMethod || entry.uiMethod || 'upi'),
    reference: String(edit.reference ?? '').trim().slice(0, 200),
    editedBy: actorName,
  };
  if (entry.currency === 'USD') {
    const banked = Number(edit.settledINR);
    if (Number.isFinite(banked) && banked >= 0) {
      body.amountSettledINR = Math.round(banked);
    }
  }
  return body;
}

/* ════════════════════ Firestore seam ════════════════════ */

/** Live ledger — every payments/ doc (team-read per rules), newest first. */
export function listenCashBook(
  cb: (rows: CashBookEntry[]) => void,
  onErr?: (e: Error) => void,
): Unsubscribe {
  return onSnapshot(
    query(collection(db, 'payments')),
    (snap) => {
      cb(sortCashBook(snap.docs.map((d) => cashBookEntryFrom(d.id, d.data()))));
    },
    (e) => onErr?.(e),
  );
}

/** Persist an entry edit (payments/ rules: team write — no rules change). */
export async function updateCashBookEntry(
  entry: CashBookEntry,
  edit: CashBookEdit,
  actorName: string,
): Promise<void> {
  await updateDoc(doc(db, 'payments', entry.id), {
    ...cashBookEditBody(entry, edit, actorName),
    editedAt: serverTimestamp(),
  });
}
